import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';

import ApplyTaskTopicAssociations from 'actions/conversation_item/task/apply_task_topic_associations';
import { ConversationTopicsBaseV2 } from 'components/customer/conversation_history/topics_v2/conversation_topics_v2';
import { TopicsMenuOptionType } from 'components/customer/conversation_history/topics_v2/conversation_topics_constants_v2';
import UpdateTaskCustomAttributes from 'actions/conversation_item/task/update_task_custom_attributes';
import { useExecuteAction } from 'components/hooks/connect_hooks';

// Rough task equivalent of ConversationTopicsActionConnectorV2. Reuses the same
// topics/custom-attributes multi-select control, but dispatches the task-scoped
// actions. Topic/attribute menu analytics (open/close/search/select) are
// conversation-specific and intentionally omitted here.
const noop = () => {};

export default function TaskTopicsActionConnector({
  className,
  customAttributesImmutable,
  customAttributesProvider,
  getTopic,
  openRequestedAt,
  task,
  taskId,
  topicsImmutable,
  topicsProvider,
}) {
  const executeAction = useExecuteAction();

  // Keyed on the observed store bindings (not the stable provider refs) so the
  // options regenerate when topics or custom attributes are added, renamed, or
  // disabled, but not on every unrelated rerender of the feed.
  const topics = useMemo(
    () =>
      topicsProvider.findAll().map(topic => ({
        id: topic.id,
        categoryOnly: !!topic.categoryOnly,
        disabled: topic.disabled,
        name: topic.getNameWithAncestry(),
        parentId: topic.parentId,
        type: TopicsMenuOptionType.TOPIC,
      })),
    [topicsImmutable, topicsProvider]
  );

  const customAttributes = useMemo(
    () =>
      _.map(customAttributesProvider.findAll({ select: ['id', 'deactivated', 'label'] }), attr => ({
        id: attr.id,
        disabled: attr.deactivated,
        name: attr.label,
        type: TopicsMenuOptionType.CUSTOM_ATTRIBUTE,
      })),
    [customAttributesImmutable, customAttributesProvider]
  );

  const handleTopicsChange = useCallback(
    updatedTopics => {
      const updatedTopicIds = _.filter(updatedTopics.optionIds, id => getTopic(id));
      const prevTopicIds = task.topicIds;
      const add = _.difference(updatedTopicIds, prevTopicIds);
      const remove = _.difference(prevTopicIds, updatedTopicIds);
      if (add.length || remove.length) {
        executeAction(ApplyTaskTopicAssociations, { taskId, topicChanges: { add, remove } });
      }

      const updatedAttributes = updatedTopics.attributeValues;
      const prevAttributes = task.customAttributes || [];
      const removedCustomAttributes = prevAttributes.filter(
        ({ id, value }) => !_.some(updatedAttributes, { id, value })
      );
      const addedCustomAttributes = updatedAttributes.filter(({ id, value }) => !_.some(prevAttributes, { id, value }));
      if (addedCustomAttributes.length || removedCustomAttributes.length) {
        executeAction(UpdateTaskCustomAttributes, {
          taskId,
          customAttributeUpdates: { add: addedCustomAttributes, remove: removedCustomAttributes },
        });
      }
    },
    [executeAction, getTopic, task, taskId]
  );

  // ConversationTopicsBaseV2 is a PureComponent; a fresh object literal here
  // would defeat it on every render.
  const conversation = useMemo(
    () => ({ topicIds: task.topicIds, customAttributes: task.customAttributes }),
    [task.topicIds, task.customAttributes]
  );

  return (
    <ConversationTopicsBaseV2
      className={className}
      conversation={conversation}
      customAttributes={customAttributes}
      dropDown
      hideSelectedOptions
      onMenuClose={noop}
      onMenuOpen={noop}
      onTopicChange={handleTopicsChange}
      onTopicSearch={noop}
      onTopicSelect={noop}
      openRequestedAt={openRequestedAt}
      topics={topics}
    />
  );
}

TaskTopicsActionConnector.propTypes = {
  className: PropTypes.string,
  // Observed store binding; a memo dependency so the options refresh when a
  // definition's label or deactivated flag changes.
  customAttributesImmutable: PropTypes.object,
  customAttributesProvider: PropTypes.shape({ findAll: PropTypes.func.isRequired }).isRequired,
  getTopic: PropTypes.func.isRequired,
  // Bump to a new value (e.g. a timestamp) to programmatically open the menu.
  openRequestedAt: PropTypes.string,
  task: PropTypes.object.isRequired,
  taskId: PropTypes.string.isRequired,
  // Observed store binding; a memo dependency so the options refresh when a
  // topic is renamed or archived.
  topicsImmutable: PropTypes.object,
  topicsProvider: PropTypes.shape({ findAll: PropTypes.func.isRequired }).isRequired,
};
